import {Injectable} from '@angular/core';
import {WizardSpecialistCompetence} from '../../models/wizard-specialist';

@Injectable()
export class ComplianceService {

    public objects = [];
    public methods = [];

    /**
     * Преобразует значение выбранной области аттестации
     * к удобному для расчета перекрытия виду.
     * @param attValue
     */
    public mapAttestationValue(attValue) {
        return attValue
        /* Сначала фильтруем аттестации, обращая внимание только на заполненные (выбран метод и объекты) */
            .filter(v => v.key && v.objects.length)
            .map(value => {
                /* Получаем объект метода, так как в области аттестации о нем только аббривиатура в поле key */
                const method = this.getMethodByKey(value.key);

                /**
                 * Коллеция методов, на которые нужно обращать внимание во время рассчета соответствия.
                 * Хранит id'шники методов в виде коллекции.
                 * @type {Array}
                 */
                const methodsToWatch = [];

                /**
                 * Логика условия, какие методы отслеживать исходит от того факта, что специалист может быть аттестован на метод,
                 * и, как следствие, на все его подметоды, либо же только на подметоды.
                 * Выбраны в качестве области аттестации могут быть тоже либо метод и, как следствие, все его подметоды,
                 * либо только какие-то определенные подметоды.
                 * Таким образом, нам достаточно сравнивать только подметоды заявленной области и аттестации специалиста,
                 * чтобы понять наличие соответствия.
                 * 1. Если и там и там выбраны методы, то и подметоды будут совпадать.
                 * 2. Если в области методы, а аттестован на подметоды, то соответствия не будет
                 * 3. Если аттестован на метод, а в области подметоды, то будет соответствие
                 * 4. Если и там и там только подметоды, то останется только сравнить соответствие
                 */
                if (value.submethods && value.submethods.length) {
                    /**
                     * Если выбраны подметоды, не важно один или все, то обращать внимание будем только на них
                     */
                    methodsToWatch.push(...value.submethods.map(sm => sm.id));
                } else {
                    /**
                     * Иначе смотреть будем все подметоды для текущего метода
                     */
                    if (method.methods && method.methods.length) {
                        methodsToWatch.push(...method.methods.map(sm => sm.id));
                    } else {
                        /**
                         * Или сам метод, если у него нет подметодов
                         */
                        methodsToWatch.push(method.id);
                    }
                }

                /**
                 * Запоминаем объекты, на которые заявлялись.
                 * Будем запоминать их в объекте, где ключами будут номера корневых объектов.
                 * @type {{}}
                 */
                const objects = {};

                /**
                 * Запоминаем выбранные объекты исключительно чтобы отобразить их затем в интерфейсе. В рассчетах они не участвуют.
                 * @type {Array}
                 */
                const choosenObjects = [];

                /**
                 * Логика запоминания следующая:
                 * 1. Получаем корневой объект и просто объект по номеру текущего из аттестации.
                 * 2. Если текущий объект, на которых заявились, является корневым,
                 * значит что автоматически мы заявились и на все его подобъекты,
                 * и флаг full остается true, означая, что заявились на весь обхект целиком.
                 * 3. Если же это не кореновой объект, то значит, что заявились только на часть объекта (full = false),
                 * но все равно считаем, что заявились и на все подобъекты текущего (для объекто 2.1 с его 2.1.1 и 2.1.2 актуально)
                 */
                value.objects.forEach(o => {
                    const root = this.getRootObjectByNumber(o.number, this.objects);
                    const obj = this.getObjectByNumber(o.number, this.objects);

                    if (!objects[root.number]) {
                        objects[root.number] = {
                            full: true,
                            list: []
                        };
                    }

                    if (root.number === o.number) {
                        objects[root.number].list = objects[root.number].list.concat(this.getObjectChildNumbers(root));
                    } else {
                        objects[root.number].full = false;
                        objects[root.number].list = objects[root.number].list.concat(this.getObjectChildNumbers(obj));
                    }

                    choosenObjects.push(o.number);
                });

                /**
                 * Аналогично компетентности специалиста, высчитываем декартого произведение
                 * методов и объектов, на которые заявлялись.
                 */
                for (const number of Object.keys(objects)) {
                    const oByM = [];
                    methodsToWatch.forEach(m => {
                        objects[number].list.forEach(o => {
                            oByM.push(`${m}_${o}`);
                        });
                    });

                    objects[number].list = oByM;
                }

                /* Запоминаем выбранные подметоды */
                let submetodsTitles = [];

                if (value.submethods && value.submethods.length && method.methods && (value.submethods.length < method.methods.length)) {
                    submetodsTitles = method.methods
                        .filter(m => value.submethods
                            .map(sm => sm.id)
                            .indexOf(m.id) >= 0)
                        .map(m => m.title);
                }

                return {
                    title: value.key,
                    objects: objects,
                    choosenObjects,
                    submetodsTitles,
                    methodsToWatch
                };
            });
    }

    /**
     * Вычислить покрытие заявленной области компетенциями специалиста.
     * На вход у нас идут нормализованные под наши нужны значения компетентности и заявленно области.
     * Под нормализацией имеется ввиду максимально полное раскрытие в виде декартовых произведений
     * всех методов и подметодов на объекты с подобъектами.
     * @param attValue
     * @param competence
     * @returns {any}
     */
    public calculateCompetence(attValue, competence) {
        const strict = true;

        return strict ? this.calculateStrictCompliance(attValue, competence) : this.calculateSoftCompliance(attValue, competence);
    }


    /**
     * Рассчитать "мягкое" соответствие, не такое жесткое, как в ПБ 372,
     * то есть заявленные обхекты по методам должны суммарно быть покрыты нужным числом специалистов.
     * @param attValue
     * @param competence
     * @returns {any}
     */
    private calculateSoftCompliance(attValue, competence: WizardSpecialistCompetence[]) {
        return attValue.map(v => {

            const objects = {};

            /**
             * Собираем методы всех специалистов в одну коллекцию
             */
            const allStaffMethods = competence.reduce((acc, c) => {
                acc.push(...c.methods);
                return acc;
            }, []);

            /* Сколько нам нужно специалистов на каждый подметод в рамках текущего метода или на метод в целом */
            /* Для РК нужно по 2 специалиста */
            const NEEDS_FOR_METHODS = v.title === 'РК' ? 2 : 1;

            /**
             * Проверям покрытие каждого метода:
             * 1 - метод покрыт нужнум числом специалистов
             * 0 - Хотя бы один специалист есть для метода, но до нужного числа NEEDS_FOR_METHODS не дотягивает
             * -1 - Нет ни одного специалиста для метода
             * @type {any[]}
             */
            const methodsCoverance = v.methodsToWatch.map( m => {
                const count = allStaffMethods.filter(sm => sm === m).length;
                if (count >= NEEDS_FOR_METHODS) {
                    return 1;
                }

                if (count === 0) {
                    return -1;
                }

                return 0;
            });

            /* Сворачиваем покрытие методов до единичного числа:
             * 1 - Все методы покрыты.
             * 0 - Как минимум один покрыт частично, остальные полностью
             * -1 - Есть непокрытые методы
             * */
            const methodCovValue = methodsCoverance.reduce((acc, cov) => {
                if (acc === 1) {
                    acc = cov;
                }

                if (acc === 0 && cov < 0) {
                    acc = cov;
                }

                return acc;
            }, 1);

            for (const number of Object.keys(v.objects)) {
                let status = 1; // 1 - Покрывает, 0 - частично, -1 - не покрывает.
                let isInvalid = true;

                /**
                 * Проверяем текущий объект в нормализованной области аттестации по текущему методы,
                 * чтобы он находился в компетенции у специалистов.
                 */
                v.objects[number].list.forEach(o => {
                    /**
                     * Для всех методов нам нужен 1 специалист для каждого объекта.
                     * @type {number}
                     */
                    const isCovered = competence.some(c => {
                        // Есть ли объект по методу у специалиста
                        return c.objectsByMethods.indexOf(o) >= 0;
                    });

                    /**
                     * Если текущий подобъект покрыт, то объект более не является абсолютно невалидным
                     * Если хотя бы для одного подобъекта не хватает спецов, то считаем как минимум частично покрытым.
                     *
                     * Логика в том, что если объект вообще не покрывается (ни один из подобъектов), то isInvalid останется true
                     * и ниже мы статус выставим как полностью невалидный.
                     * При этом, если хотя бы один подобъект не покрыт, то покрытие объекта будет как минимум частичным,
                     * остальное будет уже проверяться по значению isInvalid
                     */
                    if (isCovered) {
                        isInvalid = false;
                    } else {
                        status = 0;
                    }
                });

                if (isInvalid) {
                    status = -1;
                }

                /**
                 * Если все объекты покрыты, то теперь финальный статус зависит от покрытия методов специалистами.
                 * Если все методы покрыты, то status так и останется равен 1
                 */
                if (status === 1) {
                    status = methodCovValue;
                }

                objects[number] = {
                    full: v.objects[number].full,
                    status: status
                };
            }

            return {
                ...v,
                objects,
            };
        });
    }

    /**
     * Рассчитать соответствие, жестко следуя правилам ПБ 372,
     * то есть каждый обект должен быть покрыт нужным числом специалистов.
     * @param attValue
     * @param competence
     * @returns {any}
     */
    private calculateStrictCompliance(attValue, competence) {
        const data = attValue.map(v => {

            const objects = {};

            for (const number of Object.keys(v.objects)) {
                let status = 1; // 1 - Покрывает, 0 - частично, -1 - не покрывает.
                let isInvalid = true;

                /**
                 * Проверяем текущий объект в нормализованной области аттестации по текущему методы,
                 * чтобы он находился в компетенции у необходимого числа специалистов.
                 * Если каждый объект в итоге будет покрыт нужным количеством специалистов,
                 * то status останется равным значению полного покрытия, что означает валидность.
                 */
                v.objects[number].list.forEach(o => {
                    /**
                     * Для всех методов нам нужен 1 специалист для каждого объекта, а для РК по 2 специалиста.
                     * @type {number}
                     */
                    const COUNT_NEED = v.title === 'РК' ? 2 : 1;
                    let countStaff = COUNT_NEED;

                    competence.forEach(c => {
                        /**
                         * Если нам уже хватило специалистов, то остальные проверки не интересуют.
                         */
                        if (countStaff <= 0) {
                            return;
                        }

                        /**
                         * Если заявленный по методу объект находится в компетенции специалиста,
                         * то запоминаем этот факт в виде усеньшения числа еще необходимым спецов.
                         */
                        if (c.objectsByMethods.indexOf(o) >= 0) {
                            countStaff--;
                        }
                    });

                    /**
                     * Если хотя бы один специалист есть, но все равно не хватает до полного покрытия,
                     * то в этом случае объект по методу счиается частично покрытым и полностью невалидным быть уже не может.
                     */
                    if (countStaff < COUNT_NEED && countStaff > 0) {
                        status = 0;
                        isInvalid = false;
                    }

                    /**
                     * Если хватает специалистов, то объект более не является абсолютно невалидным
                     * Если хотя бы для одного подобъекта не хватает спецов, то считаем как минимум частично покрытым.
                     *
                     * Логика в том, что если объект вообще не покрывается (ни один из подобъектов), то isInvalid останется true
                     * и ниже мы статус выставим как полностью невалидный.
                     * При этом, если хотя бы один подобъект не покрыт, то покрытие объекта будет как минимум частичным,
                     * остальное будет уже проверяться по значению isInvalid
                     */
                    if (countStaff <= 0) {
                        isInvalid = false;
                    } else {
                        status = 0;
                    }
                });

                if (isInvalid) {
                    status = -1;
                }

                objects[number] = {
                    full: v.objects[number].full,
                    status: status
                };
            }

            return {
                ...v,
                objects,
            };
        });
        return data;
    }

    /**
     * Является ли данные таблиц соответствия валидными.
     * Не является валидными если таблица пустая или хотя бы один объект поерыт не полностью.
     * @param {any[]} data
     * @returns {any}
     */
    public isDataValid(data: any[]) {
        if (!data || !data.length) {
            return false;
        }

        return data.reduce((acc, item) => {
            for (const number in item.objects) {
                if (item.objects[number].status !== 1) {
                    acc = false;
                }
            }
            return acc;
        }, true);
    }

    private getRootObjectByNumber(number, list: any[]) {
        let toReturn = null;
        list.some(o => {
            if (o.number === number) {
                toReturn = o;
                return true;
            } else if (o.childs && o.childs.length) {
                const child = this.getRootObjectByNumber(number, o.childs);
                if (child) {
                    toReturn = o;
                    return true;
                }
            }

            return false;
        });
        return toReturn;
    }

    private getObjectByNumber(number, list: any[]) {
        let toReturn = null;
        list.some(o => {
            if (o.number === number) {
                toReturn = o;
                return true;
            } else if (o.childs && o.childs.length) {
                const child = this.getObjectByNumber(number, o.childs);
                if (child) {
                    toReturn = child;
                    return true;
                }
            }

            return false;
        });
        return toReturn;
    }

    private getMethodByKey(key) {
        return this.methods.find(m => m.short_title === key);
    }

    /**
     * Получить номера только дочерних объектов.
     * Так, например, для объекта 6 будут номера [6.1, 6.2, 6.3, ..., 6.6]
     * в для объекта 2 = [2.1.1, 2.1.2, 2.2, 2.3]. Заметь, что объект 2.1 тоже разложился на составляющие и сам не пошел в результат
     * @param object
     * @returns {any[]}
     */
    private getObjectChildNumbers(object) {
        const objects = [];

        if (object.childs && object.childs.length) {
            object.childs.forEach(o => {
                objects.push(...this.getObjectChildNumbers(o));
            });
        } else {
            objects.push(object.number);
        }

        return objects;
    }
}
